// L'albero di famiglia, la parte che non dipende né dal browser né dal server: chi sta sui
// rami (gli antenati di una persona, o i suoi discendenti) e dove si disegna. La usano la vista
// «L'albero» del quaderno e l'albero da stampare (PDF, Libro): lo stesso disegno, ovunque.
//
// Regole ferme (vedi CLAUDE.md): solo legami genitore–figlio attivi, mai un genitore dedotto da
// un nome, un gruppo o una coppia; padre a sinistra e madre a destra quando il sesso è
// registrato; un posto vuoto è un invito, mai una persona; un figlio non si inventa.

/** Quello che serve di una persona per disegnarla sull'albero. */
export type PersonaAlbero = {
  id: number; name: string; sex?: 'M' | 'F' | null; status: 'confirmed' | 'proposed';
  birth: string | null; death: string | null; photoMediaId: number | null;
};
export type LegameAlbero = { type: 'parent' | 'partner' | 'relation'; people: number[]; child: number | null; status: 'confirmed' | 'proposed' | 'rejected' };

export type ParentSide = 'father' | 'mother' | 'parent';
/** Un posto sull'albero. `parents` sono i rami che SALGONO: i genitori negli antenati, i figli
 *  nei discendenti (il disegno è lo stesso: si cresce verso l'alto). */
export type Nodo<P extends PersonaAlbero = PersonaAlbero> = {
  key: string; person: P | null; childId: number | null;
  role: ParentSide | 'self' | 'son' | 'daughter' | 'child'; side: ParentSide | 'self' | 'child'; level: number;
  parents: Nodo<P>[]; proposed: boolean; repeated: boolean; cycle: boolean; more: boolean;
};

export const parentTitle = (role: Nodo['role']): string => ({ father: 'Padre', mother: 'Madre', self: 'Persona di partenza', parent: 'Genitore', son: 'Figlio', daughter: 'Figlia', child: 'Figlio o figlia' })[role];

/** Ogni percorso è distinto: un antenato comune compare in entrambi i rami,
 * senza duplicare persone nel DB. Mai dedurre genitori da nomi, gruppi o coppie. */
export function pedigree<P extends PersonaAlbero>(rootId: number, people: P[], links: LegameAlbero[], depth = 2): Nodo<P> | null {
  const byId = new Map(people.map((p) => [p.id, p]));
  if (!byId.has(rootId)) return null;
  const limit = Math.max(1, Math.min(4, Math.floor(depth) || 2));
  const parents = new Map<number, Map<number, boolean>>();
  for (const link of links) {
    if (link.type !== 'parent' || link.status === 'rejected' || link.child === null || !byId.has(link.child)) continue;
    const row = parents.get(link.child) ?? new Map<number, boolean>();
    for (const id of link.people) if (byId.has(id)) row.set(id, (row.get(id) ?? true) && link.status === 'proposed');
    parents.set(link.child, row);
  }
  const seen = new Set<number>();
  let budget = 160;
  const visit = (id: number | null, childId: number | null, key: string, role: Nodo<P>['role'], side: Nodo<P>['side'], level: number, path: Set<number>, proposed: boolean): Nodo<P> => {
    const p = id === null ? null : byId.get(id)!;
    const node: Nodo<P> = { key, person: p, childId, role, side, level, parents: [], proposed: proposed || p?.status === 'proposed', repeated: id !== null && seen.has(id), cycle: id !== null && path.has(id), more: false };
    if (id === null) return node;
    budget--; seen.add(id);
    const records = [...(parents.get(id) ?? [])];
    if (node.cycle) return node;
    if (level >= limit || budget <= 0) { node.more = records.length > 0; return node; }
    const nextPath = new Set(path).add(id);
    const slots: Array<{ id: number | null; role: ParentSide; proposed: boolean }> = records.map(([parent, proposed]) => ({ id: parent, role: byId.get(parent)!.sex === 'M' ? 'father' : byId.get(parent)!.sex === 'F' ? 'mother' : 'parent', proposed }));
    // Non assegnare il lato mancante al primo nome disponibile.
    if (!slots.length) slots.push({ id: null, role: 'father', proposed: false }, { id: null, role: 'mother', proposed: false });
    else if (slots.length === 1) slots.push({ id: null, role: slots[0]!.role === 'father' ? 'mother' : slots[0]!.role === 'mother' ? 'father' : 'parent', proposed: false });
    const order = { father: 0, mother: 1, parent: 2 };
    slots.sort((a, b) => order[a.role] - order[b.role] || (a.id ?? Number.MAX_SAFE_INTEGER) - (b.id ?? Number.MAX_SAFE_INTEGER));
    for (const [i, slot] of slots.entries()) {
      if (budget <= 0) { node.more = true; break; }
      node.parents.push(visit(slot.id, id, `${key}.${i}`, slot.role, level === 0 ? slot.role : side, level + 1, nextPath, node.proposed || slot.proposed));
    }
    return node;
  };
  return visit(rootId, null, 'root', 'self', 'self', 0, new Set(), false);
}


/**
 * I discendenti: figli, nipoti, pronipoti — i «frutti». Si cresce verso l'alto come negli
 * antenati (i figli stanno nei `parents` del disegno), ma non ci sono posti vuoti: un figlio che
 * non è registrato non si inventa. In ordine di nascita, poi di nome.
 */
export function discendenti<P extends PersonaAlbero>(rootId: number, people: P[], links: LegameAlbero[], depth = 2): Nodo<P> | null {
  const byId = new Map(people.map((p) => [p.id, p]));
  if (!byId.has(rootId)) return null;
  const limit = Math.max(1, Math.min(4, Math.floor(depth) || 2));
  const figli = new Map<number, Map<number, boolean>>();
  for (const l of links) {
    if (l.type !== 'parent' || l.status === 'rejected' || l.child === null || !byId.has(l.child)) continue;
    for (const g of l.people) {
      if (!byId.has(g)) continue;
      const riga = figli.get(g) ?? new Map<number, boolean>();
      riga.set(l.child, (riga.get(l.child) ?? true) && l.status === 'proposed');
      figli.set(g, riga);
    }
  }
  const anno = (p: P): number => Number(p.birth?.match(/^-?(\d{4})/)?.[1] ?? 9999);
  const seen = new Set<number>();
  let budget = 160;
  const visit = (id: number, parentId: number | null, key: string, level: number, path: Set<number>, proposed: boolean): Nodo<P> => {
    const p = byId.get(id)!;
    const role: Nodo<P>['role'] = level === 0 ? 'self' : p.sex === 'M' ? 'son' : p.sex === 'F' ? 'daughter' : 'child';
    const node: Nodo<P> = { key, person: p, childId: parentId, role, side: level === 0 ? 'self' : 'child', level, parents: [], proposed: proposed || p.status === 'proposed', repeated: seen.has(id), cycle: path.has(id), more: false };
    budget--; seen.add(id);
    if (node.cycle) return node;
    const loro = [...(figli.get(id) ?? [])].map(([c, prop]) => ({ p: byId.get(c)!, prop })).sort((a, b) => anno(a.p) - anno(b.p) || a.p.name.localeCompare(b.p.name, 'it'));
    if (level >= limit || budget <= 0) { node.more = loro.length > 0; return node; }
    const next = new Set(path).add(id);
    for (const [i, f] of loro.entries()) {
      if (budget <= 0) { node.more = true; break; }
      node.parents.push(visit(f.p.id, id, `${key}.${i}`, level + 1, next, node.proposed || f.prop));
    }
    return node;
  };
  return visit(rootId, null, 'root', 0, new Set(), false);
}

export const ARTE = {
  medaglione: 96,   // diametro del ritratto
  posto: 156,       // larghezza di un posto all'ultimo livello (medaglione + cartiglio)
  riga: 214,        // distanza fra una generazione e l'altra
  chioma: 110,      // spazio sopra l'ultima generazione, per le foglie
  tronco: 230,      // dal medaglione di partenza al terreno
  piede: 120,       // terreno e nastro col titolo
  cupola: 0.32,     // quanto scendono i lati della chioma, in righe
} as const;

export type Punto = { x: number; y: number };
export type Foglia = { x: number; y: number; angolo: number; scala: number; tono: 0 | 1 | 2 };
export type Ramo = { d: string; spessore: number; livello: number; incerto: boolean };
export type Disegno<P extends PersonaAlbero = PersonaAlbero> = {
  larghezza: number; altezza: number;
  posti: Array<{ nodo: Nodo<P>; centro: Punto }>;
  rami: Ramo[]; tronco: string; corteccia: string[]; radici: string[]; foglie: Foglia[];
  chioma: Array<{ x: number; y: number; r: number }>;
  terreno: number; nastro: Punto;
};

/** Generatore con seme (mulberry32): stesse foglie per la stessa persona e la stessa profondità. */
function generatore(seme: number): () => number {
  let a = seme >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const r1 = (n: number): number => Math.round(n * 10) / 10;

/** Lo spessore di un ramo: grosso vicino al tronco, sottile verso i bisnonni. */
export const spessoreRamo = (livello: number): number => [0, 24, 15, 9, 6][Math.min(livello, 4)]!;

export function disegnaAlbero<P extends PersonaAlbero>(albero: Nodo<P>): Disegno<P> {
  // 1. Le larghezze: ogni foglia dell'albero genealogico (una persona senza genitori mostrati,
  //    o un posto vuoto) occupa un posto; un genitore sta sopra la metà dei suoi.
  const campate = new Map<string, number>();
  let livelli = 0;
  const misura = (n: Nodo<P>): number => {
    livelli = Math.max(livelli, n.level);
    const c = n.parents.length ? n.parents.reduce((s, p) => s + misura(p), 0) : ARTE.posto;
    campate.set(n.key, c);
    return c;
  };
  const totale = misura(albero);
  const larghezza = Math.max(560, totale + 96);
  const cima = ARTE.chioma + ARTE.medaglione / 2;
  const yPartenza = cima + livelli * ARTE.riga;
  const terreno = yPartenza + ARTE.tronco;
  const altezza = terreno + ARTE.piede;
  const centroX = larghezza / 2;
  const meta = Math.max(1, totale / 2);

  // 2. I posti: generazione per generazione verso l'alto, e la chioma a cupola (i lati
  //    scendono un poco, come nei rami veri che si allargano e si piegano).
  const posti: Disegno<P>['posti'] = [];
  const centri = new Map<string, Punto>();
  const colloca = (n: Nodo<P>, sinistra: number): void => {
    const c = campate.get(n.key)!;
    const x = sinistra + c / 2;
    const scarto = (x - centroX) / meta;
    const y = yPartenza - n.level * ARTE.riga + (n.level > 0 ? scarto * scarto * ARTE.cupola * ARTE.riga : 0);
    const centro = { x: r1(x), y: r1(y) };
    centri.set(n.key, centro);
    posti.push({ nodo: n, centro });
    let s = sinistra;
    for (const p of n.parents) { colloca(p, s); s += campate.get(p.key)!; }
  };
  colloca(albero, centroX - totale / 2);

  // 3. I rami: da ogni persona verso ciascun genitore, una curva che parte verticale (cresce
  //    dal medaglione) e arriva al genitore da sotto. Incerto = da confermare o posto vuoto.
  const rami: Ramo[] = [];
  const percorri = (n: Nodo<P>): void => {
    const a = centri.get(n.key)!;
    for (const p of n.parents) {
      const b = centri.get(p.key)!;
      const dy = a.y - b.y;
      rami.push({
        d: `M${a.x},${a.y} C${a.x},${r1(a.y - dy * 0.55)} ${b.x},${r1(b.y + dy * 0.5)} ${b.x},${b.y}`,
        spessore: spessoreRamo(p.level), livello: p.level, incerto: !p.person || p.proposed,
      });
      percorri(p);
    }
  };
  percorri(albero);

  // 4. Il tronco: largo al piede, stretto sotto il medaglione, con le radici che si aprono.
  const p0 = centri.get(albero.key)!;
  const baseMeta = 62, cimaMeta = 20, x = p0.x;
  const tronco = [
    `M${x - cimaMeta},${p0.y}`,
    `C${x - cimaMeta - 4},${r1(p0.y + ARTE.tronco * 0.45)} ${x - baseMeta + 18},${r1(terreno - 40)} ${x - baseMeta},${terreno}`,
    `L${x + baseMeta},${terreno}`,
    `C${x + baseMeta - 18},${r1(terreno - 40)} ${x + cimaMeta + 4},${r1(p0.y + ARTE.tronco * 0.45)} ${x + cimaMeta},${p0.y}`,
    'Z',
  ].join(' ');
  const corteccia = [
    `M${x - 6},${p0.y + 40} C${x - 10},${r1(p0.y + ARTE.tronco * 0.5)} ${x - 22},${terreno - 50} ${x - 30},${terreno - 8}`,
    `M${x + 8},${p0.y + 60} C${x + 12},${r1(p0.y + ARTE.tronco * 0.55)} ${x + 20},${terreno - 60} ${x + 26},${terreno - 14}`,
  ];
  const radici = [
    `M${x - baseMeta + 6},${terreno - 6} C${x - baseMeta - 20},${terreno + 2} ${x - baseMeta - 44},${terreno + 4} ${x - baseMeta - 70},${terreno + 14}`,
    `M${x + baseMeta - 6},${terreno - 6} C${x + baseMeta + 20},${terreno + 2} ${x + baseMeta + 44},${terreno + 4} ${x + baseMeta + 70},${terreno + 14}`,
    `M${x - 14},${terreno - 2} C${x - 20},${terreno + 12} ${x - 34},${terreno + 20} ${x - 46},${terreno + 26}`,
    `M${x + 16},${terreno - 2} C${x + 22},${terreno + 12} ${x + 36},${terreno + 20} ${x + 50},${terreno + 26}`,
  ];

  // 5. Le foglie: lungo ogni ramo e in una corona attorno a ogni medaglione (di più verso
  //    l'alto, dove un albero vero è più folto). Seme = persona di partenza + profondità.
  const caso = generatore(((albero.person?.id ?? 7) * 7919 + livelli * 104729) | 0);
  const foglie: Foglia[] = [];
  const tono = (): 0 | 1 | 2 => (caso() < 0.45 ? 0 : caso() < 0.7 ? 1 : 2);
  for (const n of posti) {
    if (n.nodo.level === 0) continue;
    const quante = n.nodo.person ? 16 + Math.floor(caso() * 6) : 0; // un posto vuoto resta aria
    for (let i = 0; i < quante; i++) {
      const ang = Math.PI * (1.05 + caso() * 0.9) + (caso() < 0.3 ? Math.PI * 0.95 : 0); // soprattutto sopra
      const raggio = ARTE.medaglione / 2 + 10 + caso() * 30;
      foglie.push({ x: r1(n.centro.x + Math.cos(ang) * raggio * 1.25), y: r1(n.centro.y + Math.sin(ang) * raggio), angolo: Math.round(caso() * 360), scala: r1(0.8 + caso() * 0.7), tono: tono() });
    }
  }
  for (const n of posti) {
    const a = n.centro;
    for (const p of n.nodo.parents) {
      const b = centri.get(p.key)!;
      const quante = p.person && !p.proposed ? 10 + Math.floor(caso() * 5) : 0;
      for (let i = 0; i < quante; i++) {
        const t = 0.3 + caso() * 0.55;
        const px = a.x + (b.x - a.x) * t * t * (3 - 2 * t), py = a.y + (b.y - a.y) * t; // lungo la curva, all'incirca
        const lato = caso() < 0.5 ? -1 : 1;
        foglie.push({ x: r1(px + lato * (10 + caso() * 16)), y: r1(py + (caso() - 0.5) * 18), angolo: Math.round(caso() * 360), scala: r1(0.7 + caso() * 0.6), tono: tono() });
      }
    }
  }
  // 6. La chioma: una massa verde tenue dietro le persone, come le fronde dipinte. Solo dove
  //    c'è una persona vera: un posto vuoto resta aria.
  const chioma: Disegno<P>['chioma'] = [];
  for (const n of posti) {
    if (n.nodo.level === 0 || !n.nodo.person) continue;
    chioma.push({ x: n.centro.x, y: r1(n.centro.y - 6), r: r1(ARTE.medaglione * (0.95 + caso() * 0.25)) });
    chioma.push({ x: r1(n.centro.x + (caso() - 0.5) * 90), y: r1(n.centro.y - 40 - caso() * 30), r: r1(ARTE.medaglione * (0.6 + caso() * 0.3)) });
  }
  return { larghezza, altezza, posti, rami, tronco, corteccia, radici, foglie, chioma, terreno, nastro: { x: centroX, y: terreno + 62 } };
}

/**
 * Toglie i posti vuoti oltre una generazione (1 genitori, 2 nonni, 3 bisnonni; 0 = nessuno).
 * Le persone vere restano tutte: si pota solo l'invito «Chi era?», mai un antenato registrato.
 */
export function potaVuoti<P extends PersonaAlbero>(n: Nodo<P>, fino: number): Nodo<P> {
  return { ...n, parents: n.parents.filter((p) => p.person || p.level <= fino).map((p) => potaVuoti(p, fino)) };
}

/** L'anno di una data EDTF («1952-03-10», «1952~», «195X»): solo se c'è un anno intero. */
export function anno(edtf: string | null): string | null {
  const m = edtf?.match(/^-?(\d{4})/);
  return m ? m[1]! : null;
}

/** «1921 – 1998», «n. 1952», «† 1998», oppure niente. Mai l'età. */
export function anni(nascita: string | null, morte: string | null): string {
  const a = anno(nascita), b = anno(morte);
  if (a && b) return `${a} – ${b}`;
  if (a) return `n. ${a}`;
  if (b) return `† ${b}`;
  return '';
}

const esc = (s: string): string => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

/** Il disegno (tronco, rami, foglie, terreno, nastro) come SVG autonomo: per il PDF, dove le
 *  persone e il titolo li compone Typst con i suoi caratteri. Colori espliciti, niente CSS. */
export function svgArte(d: Disegno, opts: { titolo?: string } = {}): string {
  const out: string[] = [];
  out.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${d.larghezza} ${d.altezza}" width="${d.larghezza}" height="${d.altezza}">`);
  out.push(`<ellipse cx="${d.nastro.x}" cy="${d.terreno + 8}" rx="${Math.min(d.larghezza * 0.42, 340)}" ry="22" fill="#203B34" fill-opacity="0.12"/>`);
  for (const c of d.chioma) out.push(`<circle cx="${c.x}" cy="${c.y}" r="${c.r}" fill="#D5E2CE" fill-opacity="0.55"/>`);
  for (const r of d.radici) out.push(`<path d="${r}" fill="none" stroke="#52604D" stroke-width="9" stroke-linecap="round"/>`);
  out.push(`<path d="${d.tronco}" fill="#5B6A55" stroke="#46533F" stroke-width="2"/>`);
  for (const c of d.corteccia) out.push(`<path d="${c}" fill="none" stroke="#FFFFFF" stroke-opacity="0.16" stroke-width="2" stroke-linecap="round"/>`);
  for (const r of d.rami) out.push(r.incerto
    ? `<path d="${r.d}" fill="none" stroke="#B9C3B2" stroke-width="3" stroke-dasharray="1 9" stroke-linecap="round"/>`
    : `<path d="${r.d}" fill="none" stroke="#5B6A55" stroke-width="${r.spessore}" stroke-linecap="round"/>`);
  const toni = ['#6E8F74', '#4F7A5B', '#97B489'];
  for (const f of d.foglie) out.push(`<path d="M0,-10 C7,-6 7,6 0,10 C-7,6 -7,-6 0,-10 Z" fill="${toni[f.tono]}" stroke="#203B34" stroke-opacity="0.25" stroke-width="0.6" transform="translate(${f.x} ${f.y}) rotate(${f.angolo}) scale(${f.scala})"/>`);
  const nx = d.nastro.x, ny = d.nastro.y, nl = Math.min(d.larghezza * 0.36, 250);
  out.push(`<path d="M${nx - nl - 34},${ny - 6} l34,-10 l0,34 l-34,6 l12,-15 Z" fill="#E4DCC0" stroke="#C9A55A" stroke-width="1.2"/>`);
  out.push(`<path d="M${nx + nl + 34},${ny - 6} l-34,-10 l0,34 l34,6 l-12,-15 Z" fill="#E4DCC0" stroke="#C9A55A" stroke-width="1.2"/>`);
  out.push(`<path d="M${nx - nl},${ny - 16} Q${nx},${ny - 32} ${nx + nl},${ny - 16} L${nx + nl},${ny + 18} Q${nx},${ny + 2} ${nx - nl},${ny + 18} Z" fill="#F2EDDA" stroke="#C9A55A" stroke-width="1.5"/>`);
  if (opts.titolo) out.push(`<text x="${nx}" y="${ny + 6}" text-anchor="middle" font-family="Georgia, serif" font-size="26" fill="#173F35">${esc(opts.titolo)}</text>`);
  out.push('</svg>');
  return out.join('\n');
}
